import { useEffect, useRef, useState, useCallback } from "react";
import { Loader2, MapPin, Clock, Flag, PawPrint } from "lucide-react";
import TripMap from "@/components/TripMap";

const fmtAgo = (secs) => {
  if (secs == null) return "no updates yet";
  const s = Math.max(0, Math.round(secs));
  if (s < 60) return `${s}s ago`;
  if (s < 3600) return `${Math.round(s / 60)} min ago`;
  return `${Math.round(s / 3600)}h ago`;
};

// Reusable live view used by admin, customer portal and the public share link.
// `load` is an async function returning the live payload. Polls every 12s.
export const TripLiveView = ({ load, mapHeight = 420, showStamps = true }) => {
  const [live, setLive] = useState(null);
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(true);
  const timer = useRef(null);

  const refresh = useCallback(async () => {
    try {
      const data = await load();
      setLive(data); setErr("");
    } catch (e) {
      setErr(e?.response?.data?.detail || "Could not load this trip right now.");
    } finally {
      setLoading(false);
    }
  }, [load]);

  useEffect(() => {
    refresh();
    timer.current = setInterval(refresh, 12000);
    return () => clearInterval(timer.current);
  }, [refresh]);

  if (loading) return <div className="flex items-center gap-2 text-charcoal-500 py-10 justify-center" data-testid="trip-live-loading"><Loader2 className="animate-spin" size={18} /> Loading live map…</div>;
  if (err) return <div className="text-sm text-red-600 py-6" data-testid="trip-live-error">{err}</div>;

  const t = live.trip;
  const earned = (live.stamps || []).filter((s) => s.status !== "suggested");
  return (
    <div className="space-y-4" data-testid="trip-live-view">
      <div className="flex flex-wrap items-center gap-3 text-sm">
        <span className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full font-medium ${live.stale ? "bg-charcoal-100 text-charcoal-600" : "bg-green-100 text-green-700"}`} data-testid="trip-status-pill">
          <span className={`w-2 h-2 rounded-full ${live.stale ? "bg-charcoal-400" : "bg-green-500 animate-pulse"}`} />
          {t.status === "ended" ? "Trip ended" : live.stale ? "Offline" : "Live"}
        </span>
        <span className="inline-flex items-center gap-1.5 text-charcoal-500"><Clock size={14} /> Updated {fmtAgo(live.seconds_since_update)}</span>
        {live.distance_remaining_km != null && (
          <span className="inline-flex items-center gap-1.5 text-charcoal-500"><MapPin size={14} /> {live.distance_remaining_km} km to {t.destination?.name || "destination"}</span>
        )}
        {(t.pets || []).length > 0 && (
          <span className="inline-flex items-center gap-1.5 text-terracotta"><PawPrint size={14} /> {t.pets.map((p) => p.name).filter(Boolean).join(", ")}</span>
        )}
      </div>

      <TripMap live={live} height={mapHeight} />

      {showStamps && (
        <div data-testid="trip-stamps">
          <h4 className="font-serif text-lg text-charcoal-800 mb-2 flex items-center gap-2"><Flag size={16} /> Journey stamps</h4>
          {earned.length === 0 ? (
            <p className="text-sm text-charcoal-500">No cities stamped yet — they appear as {t.pets?.[0]?.name || "your pet"} actually passes each one.</p>
          ) : (
            <ol className="relative border-l-2 border-sand-200 ml-2 space-y-3">
              {earned.map((s) => (
                <li key={s.id} className="ml-4" data-testid={`stamp-${s.id}`}>
                  <span className={`absolute -left-[9px] w-4 h-4 rounded-full border-2 border-white ${s.kind === "border" ? "bg-red-600" : "bg-green-600"}`} />
                  <div className="text-sm font-medium text-charcoal-800">{s.name} {s.kind === "border" && <span className="text-red-600 text-xs">· border</span>}</div>
                  <div className="text-xs text-charcoal-400">
                    {s.source === "gps" ? "Confirmed by GPS" : "Added by our team"}
                    {s.stamped_at ? ` · ${new Date(s.stamped_at).toLocaleString()}` : ""}
                  </div>
                </li>
              ))}
            </ol>
          )}
        </div>
      )}
    </div>
  );
};

export default TripLiveView;
